import { useEffect, useState } from 'preact/hooks';
import { useRouter } from 'preact-router';
import { useDispatch } from 'react-redux';

import { useLatestActiveTime } from '@/hooks/useLatestActiveDateTime';
import { useSelector } from '@/redux/helper';
import { setGuestSessionLockUntil } from '@/redux/slices/deviceSettings/actions';
import { selectIsGuestSessionLocked } from '@/redux/slices/deviceSettings/selectors';

import {
  dismissMembersOnlyWarningModalWithType,
  setMembersOnlyWarningModalState,
} from '../MembersOnlyWarningModal/redux/actions';

const isEligiblePath = (path: string) => path.startsWith('/home') || path.startsWith('/highlights');

/**
 * Controls guest-session modal timing and lock enforcement at the app level.
 * - If guest is locked, opens the non-dismissible modal immediately.
 * - If not locked and on /home, arms a one-shot timer based on remoteConfig.
 */

const GuestExperienceController = () => {
  const dispatch = useDispatch();
  const logged = useSelector((state) => state.user.logged);
  const timeoutMs = useSelector((state) => state.remoteConfig.timeoutGuestUserAfterMs);
  const lockForMs = useSelector((state) => state.remoteConfig.resetGuestUserTimeoutAfterMs);
  const [now] = useLatestActiveTime();
  const isLocked = useSelector((state) => selectIsGuestSessionLocked(state, now));
  const { visible, type } = useSelector((state) => state.modals.membersOnlyWarningModal);

  const [{ path }] = useRouter();
  const [isTimeToLock, setIsTimeToLock] = useState(false);

  const armDelayMs = logged || isLocked ? null : timeoutMs;
  const isGuestSessionOpen = visible && type === 'guest-session';

  // countdown to flag lock state
  useEffect(() => {
    if (armDelayMs == null || isLocked) return undefined;
    const id = setTimeout(() => {
      setIsTimeToLock(true);
      dispatch(setGuestSessionLockUntil(Date.now() + lockForMs));
    }, armDelayMs);
    return () => clearTimeout(id);
  }, [armDelayMs, lockForMs, isLocked, dispatch]);

  // open on navigation to an eligible path while locked (e.g., came from /schedule to /home)
  useEffect(() => {
    const delayMs = isLocked ? 1000 : isTimeToLock ? 0 : null;
    if (logged || !isEligiblePath(path ?? '') || delayMs == null) {
      dispatch(dismissMembersOnlyWarningModalWithType('guest-session'));
      return undefined;
    }

    if (isGuestSessionOpen) {
      return undefined;
    }

    const scheduledId = setTimeout(() => {
      dispatch(setMembersOnlyWarningModalState({ type: 'guest-session', redirectOnDismiss: false }));
    }, delayMs);
    return () => {
      clearTimeout(scheduledId);
    };
  }, [logged, isLocked, isGuestSessionOpen, isTimeToLock, path, dispatch]);

  return null;
};

export default GuestExperienceController;
